/** Reading a job description the API returned: the same wording as the company front. */

import { normalizeToolProvider } from './templateFormData';
import { templateText } from './templateTexts';

export const getTemplateTitle = (template) =>
  template?.name || template?.title || '';

export const getTemplateQualification = (template) =>
  template?.qualification || template?.title || '';

// "Le Marin, Martinique": only a populated address carries a city.
export const formatTemplateCity = (location) => {
  if (!location || typeof location !== 'object') return '';
  return [location.city, location.department]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
};

export const formatTemplateAddress = (location) => {
  if (!location) return '';
  if (typeof location === 'string') return location;
  const { street, postcode, city, department } = location;
  return [street, postcode, city, department].filter(Boolean).join(' ');
};

const hourlyRateFormatter = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// « 12,31 € », and « 12,31 € · indexé au SMIC » for an indexed template.
export const formatHourlyRate = (wage, isIndexedToMinimumWage) => {
  const rate =
    typeof wage === 'string' && wage.trim() !== '' ? Number(wage) : wage;
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return '';
  return templateText(
    isIndexedToMinimumWage ? 'format.rateIndexed' : 'format.rate',
    { rate: hourlyRateFormatter.format(rate) }
  );
};

export const formatTemplateRate = (template) =>
  formatHourlyRate(
    template?.salary?.wage_per_hour,
    !!template?.isIndexedToMinimumWage
  );

// The grid card appends « /h » itself and has no room for the SMIC mention.
export const formatTemplateCardRate = (template) => {
  const wage = template?.salary?.wage_per_hour;
  return typeof wage === 'number' && Number.isFinite(wage)
    ? `${hourlyRateFormatter.format(wage)}€`
    : '';
};

export const formatTemplateList = (list) =>
  Array.isArray(list) && list.length > 0 ? list.join(', ') : '';

// An old template stores plain names: like the form, it reads them as provided
// by the company.
export const formatTemplateTools = (tools) =>
  Array.isArray(tools)
    ? tools
        .map((tool) => {
          const name = typeof tool === 'string' ? tool : tool?.name;
          if (typeof name !== 'string' || name.trim() === '') return '';
          const provider = templateText(
            `format.toolProvider.${normalizeToolProvider(tool?.providedBy)}`
          );
          return `${name.trim()} (${provider})`;
        })
        .filter(Boolean)
        .join(', ')
    : '';

// Missions only know tool names, whoever provides them.
export const toToolNames = (tools) =>
  Array.isArray(tools)
    ? tools
        .map((tool) => (typeof tool === 'string' ? tool : tool?.name))
        .filter((name) => typeof name === 'string' && name.trim() !== '')
        .map((name) => name.trim())
    : [];

// « William Meslien · Responsable »; no manager, no line.
export const formatContact = (name, poste) => {
  const cleanName = typeof name === 'string' ? name.trim() : '';
  const cleanPoste = typeof poste === 'string' ? poste.trim() : '';
  if (!cleanName) return '';
  return cleanPoste
    ? templateText('format.contact', { name: cleanName, poste: cleanPoste })
    : cleanName;
};

export const formatTemplateContact = (template) =>
  formatContact(template?.contact?.name, template?.contact?.poste);

/** « A », « A et B », then « A, B et 1 autre » / « A, B et 4 autres ». */
export const formatTemplateTasks = (tasks) => {
  const labels = (Array.isArray(tasks) ? tasks : [])
    .map((task) => (typeof task === 'string' ? task : task?.label))
    .filter((label) => typeof label === 'string' && label.trim() !== '')
    .map((label) => label.trim());
  if (labels.length === 0) return templateText('format.noTask');
  if (labels.length === 1) return labels[0];
  const [first, second] = labels;
  if (labels.length === 2)
    return templateText('format.tasksTwo', { first, second });
  const count = labels.length - 2;
  return templateText(
    count === 1 ? 'format.tasksMoreOne' : 'format.tasksMoreOther',
    { first, second, count }
  );
};

/** The dress code as typed; a template saved before it existed has none. */
export const formatDressCode = (dressCode) =>
  typeof dressCode === 'string' && dressCode.trim() !== ''
    ? dressCode
    : templateText('format.dressCodeMissing');

// Tools, licences and languages read « Aucun » when there are none.
export const orNone = (value) => value || templateText('format.none');

// Search on what the card shows, accents and case aside.
const searchable = (value) =>
  typeof value === 'string'
    ? value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
    : '';

export const filterTemplates = (templates, query) => {
  const words = searchable(query).split(/\s+/).filter(Boolean);
  const list = Array.isArray(templates) ? templates : [];
  if (words.length === 0) return list;
  return list.filter((template) => {
    const text = searchable(
      [
        getTemplateTitle(template),
        getTemplateQualification(template),
        formatTemplateCity(template?.location),
      ].join(' ')
    );
    return words.every((word) => text.includes(word));
  });
};
